import { http } from '@/lib/http';
import { asLockConflict } from '@/lib/locks/api';
import type { BulkAction, IPage, Passenger, PassengerFilters, PassengerRevealed } from './types';

function manifestPath(caseUrn: string): string {
  return `v1/cases/${encodeURIComponent(caseUrn)}/passengers`;
}

// Cursor-paginated passengers list (FROZEN contract — `?limit&cursor` →
// `{ items, nextCursor }`, no `total`). The cursor is opaque: the caller echoes
// a prior page's `nextCursor` back verbatim and never constructs one. The
// `status` filter is pushed to the server so infinite-scroll pages over the
// FILTERED set; `limit` is left to the BFF default (50, hard max 200).
export async function listPassengers(
  caseUrn: string,
  filters: PassengerFilters,
  cursor?: string,
): Promise<IPage<Passenger>> {
  const search = new URLSearchParams();
  if (filters.status) search.set('status', filters.status);
  if (cursor) search.set('cursor', cursor);
  const qs = search.toString();
  const path = qs ? `${manifestPath(caseUrn)}?${qs}` : manifestPath(caseUrn);
  return http.get(path).json<IPage<Passenger>>();
}

interface BulkRequest {
  passengerUrns: readonly string[];
  action: BulkAction;
}

export async function bulkApply(caseUrn: string, request: BulkRequest): Promise<void> {
  await http.post(`${manifestPath(caseUrn)}/bulk`, { json: request });
}

export async function revealField(
  caseUrn: string,
  passengerUrn: string,
  field: PassengerRevealed['field'],
): Promise<PassengerRevealed> {
  // The BFF audits every reveal per TDS §14 anti-pattern 5.
  return http
    .post(
      `${manifestPath(caseUrn)}/${encodeURIComponent(passengerUrn)}/reveal/${field}`,
    )
    .json<PassengerRevealed>();
}

export async function requestTransfer(caseUrn: string, passengerUrn: string): Promise<void> {
  // A reassign is a write — the BFF auto-acquires the passenger's PNR (sub-case)
  // lock. On contention it 409s `{ error:'locked', holder }`; surface it as a
  // LockConflictError so the drawer can show "PNR x is being edited by <holder>".
  try {
    await http.post(
      `${manifestPath(caseUrn)}/${encodeURIComponent(passengerUrn)}/transfer-request`,
    );
  } catch (err) {
    await asLockConflict(err);
  }
}
